import { BasiqApi } from "./basiq.api";
import {
  BasiqAccountResponse,
  BasiqInstitutionResponse,
  BasiqResponse,
  BasiqTransactionParams,
} from "../../../../models/types/basiq.types";
import { AccountView } from "@bitwarden/web-vault/app/models/view/account/account.view";
import { Injectable } from "@angular/core";
import { BasiqConfigType } from "@bitwarden/web-vault/app/models/types/environement-config.type";

const basiqConfig = process.env.BASIQ as unknown as BasiqConfigType;

@Injectable({
  providedIn: "root",
})
/** This is to get any sort of data from basiq. Transactions, accounts, connections ets...*/
export class BasiqDataApi extends BasiqApi {
  constructor() {
    super();
  }
  /**
   * @async
   * @returns {Promise<BasiqResponse>} - A promise that resolves to the Basiq response containing account information.
   * @throws {Error} - Throws an error if the request fails, logging the error and showing a warning message.
   */
  async accounts(): Promise<BasiqAccountResponse> {
    try {
      const data = { action: "GET", endpoint: "accounts" };
      return await this.getProxyGlossApi(data);
    } catch (error) {
      await this.glossSyncService.logError([], error, `Retrieving accounts from Basiq`, null);
      this.glossSyncService.confirmationDialog.notify("error", "error", "somethingWentWrong");
      return null;
    }
  }

  async getTransactionsFromBasiq(
    lastTransactionDate: string,
    account: AccountView,
    nextPath?: string,
  ): Promise<BasiqResponse> {
    try {
      const transactionsParams: BasiqTransactionParams = {
        lastTransactionDate,
        accountId: account.basiqAccountId,
        nextPath,
        endpoint: "transactions",
      };
      return await this.getTransactions(transactionsParams);
    } catch (error) {
      await this.glossSyncService.logError([], error, `Retrieving transactions from Basiq`, null);
    }
  }

  /**
   * @async
   * @param {BasiqTransactionParams} transactionParams - The endpoint from which to fetch transaction information.
   * @returns {Promise<any>} - A promise that resolves to the transaction data.
   * @throws {Error} - Throws an error if the request fails, logging the error.
   */
  async getTransactions(transactionParams: BasiqTransactionParams): Promise<BasiqResponse> {
    try {
      return await this.getProxyGlossApi(transactionParams);
    } catch (error) {
      await this.glossSyncService.logError(
        [],
        error,
        `Retrieving transactions from Basiq with params : ${JSON.stringify(transactionParams)}`,
        null,
      );
      throw error;
    }
  }

  /**
   * @async
   * @param {string} institutionId - The ID of the institution to fetch.
   * @returns {Promise<BasiqInstitutionResponse>} - A promise that resolves to the Basiq response containing the institution.
   *
   * @throws {Error} - Throws an error if the request fails, logging the error.
   */
  async institutionById(institutionId: string): Promise<BasiqInstitutionResponse> {
    try {
      const data = { endpoint: `institution`, institutionId };
      return await this.getProxyGlossApi(data);
    } catch (error) {
      await this.glossSyncService.logError(
        [],
        error,
        `Retrieving institution from Basiq with id : ${institutionId}`,
        null,
      );
      throw error;
    }
  }
}
